import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import type { Configs } from "../DatepickerV2/types";

/**
 * Translated shortcut + footer labels for any `Datepicker`/`DatepickerV2`.
 *
 * The keys sit under the repo's shared-string prefix (`global_datepicker_*`) so
 * every date-range picker draws from one set. Four page-scoped copies of these
 * same seven strings already exist (`clientsPage_`, `leadsPage_`, `exportPage_`,
 * `performanceReport_datepicker_*`) — new callsites should use this hook rather
 * than adding a fifth.
 *
 * Returns `Configs` as-is, so it can be handed straight to the picker's
 * `configs` prop and to `getShortcutButtonLabel`.
 */
export const useDatepickerTranslations = (): Configs => {
  const { t } = useTranslation();

  return useMemo(
    () => ({
      shortcuts: {
        today: t("global_datepicker_today", "Today"),
        yesterday: t("global_datepicker_yesterday", "Yesterday"),
        past: (period: number) =>
          t("global_datepicker_past", "Last {{period}} days", { period }),
        currentMonth: t("global_datepicker_currentMonth", "This month"),
        pastMonth: t("global_datepicker_pastMonth", "Last month"),
      },
      footer: {
        cancel: t("global_datepicker_cancel", "Cancel"),
        apply: t("global_datepicker_apply", "Apply"),
      },
    }),
    [t],
  );
};
